import React from 'react';

interface PageContainerProps {
    children: React.ReactNode;
    className?: string;
    noPadding?: boolean;
}

/**
 * Unified page container for all mobile pages
 * Enforces consistent spacing and layout standards
 */
export const PageContainer: React.FC<PageContainerProps> = ({
    children,
    className = '',
    noPadding = false
}) => {
    return (
        <div className={`
            min-h-[100dvh] 
            bg-black 
            text-white 
            pt-safe 
            pb-20
            ${noPadding ? 'pt-4' : 'px-4 pt-4'}
            ${className}
        `}>
            {children}
        </div>
    );
};

interface PageHeaderProps {
    title?: string;
    left?: React.ReactNode;
    right?: React.ReactNode;
    className?: string;
}

/**
 * Standardized page header (56px height)
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
    title,
    left,
    right,
    className = ''
}) => {
    return (
        <div className={`
            h-14
            flex 
            items-center 
            justify-between 
            px-4
            -mx-4
            mb-4
            ${className}
        `}>
            <div className="flex-shrink-0">
                {left}
            </div>
            {title && (
                <h1 className="text-xl font-bold flex-1 text-center">
                    {title}
                </h1>
            )}
            <div className="flex-shrink-0 flex items-center gap-3">
                {right}
            </div>
        </div>
    );
};

interface PageSectionProps {
    children: React.ReactNode;
    className?: string;
    title?: string;
}

/**
 * Page section with consistent spacing
 */
export const PageSection: React.FC<PageSectionProps> = ({
    children,
    className = '',
    title
}) => {
    return (
        <div className={`mb-6 ${className}`}>
            {title && (
                <h2 className="text-lg font-bold mb-3">
                    {title}
                </h2>
            )}
            {children}
        </div>
    );
};
